/** Irrevocably fixed by the euro adoption law. Never a configurable value. */
export const BGN_PER_EUR = 1.95583;

export function eurToBgn(eur: number): number {
  return Math.round(eur * BGN_PER_EUR * 100) / 100;
}

export function formatMoney(amount: number, currency: 'EUR' | 'BGN', locale = 'bg-BG'): string {
  return formatAmount(amount, currency, locale);
}

/**
 * Any ISO currency, for the one amount whose currency is not ours to decide: the subscription
 * price comes back from Stripe in whatever the price was created in.
 */
export function formatAmount(amount: number, currency: string, locale = 'bg-BG'): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(amount);
}

/**
 * The same amount with the cents dropped, for places where a figure is a position on a scale
 * rather than a sum anybody will reconcile — chart axes, mostly.
 *
 * `formatMoney` fixes two fraction digits because every figure it was written for is money someone
 * pays, and "1 234,5 €" on an invoice is a typo. An axis labelled "0,00 € · 2 500,00 € · 5 000,00 €"
 * is three times as wide as the information in it.
 */
export function formatMoneyCompact(amount: number, currency: 'EUR' | 'BGN', locale = 'bg-BG'): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(amount);
}

/**
 * Amounts are shown in euro with the lev equivalent alongside, which the euro-adoption rules
 * require in consumer-facing contexts through the transition period.
 */
export function formatDual(eur: number, locale = 'bg-BG'): string {
  return `${formatMoney(eur, 'EUR', locale)} (${formatMoney(eurToBgn(eur), 'BGN', locale)})`;
}

/**
 * Today as an ISO date, from local calendar parts.
 *
 * `new Date().toISOString().slice(0, 10)` is the UTC date, which east of Greenwich is yesterday for
 * the first hours of every day — so an invoice marked paid at 00:20 in Sofia landed on the previous
 * day, and in December that puts it in the wrong tax year.
 */
/**
 * A fraction as a percentage, in the reader's locale: 0.148 becomes "14,8%" in Bulgarian and
 * "14.8%" in English. Trailing zeros are dropped so a plain 8% is not dressed up as 8,00% beside a
 * genuine 3,5%.
 *
 * Two digits by default because a statutory rate is exact and "3,5%" and "3,55%" are different
 * rules. A share of a whole is not — pass 0 for one, or a chart legend reads "47,28%".
 */
export function formatRate(fraction: number, locale = 'bg-BG', maxDigits = 2): string {
  return new Intl.NumberFormat(locale, {
    style: 'percent',
    maximumFractionDigits: maxDigits,
  }).format(fraction);
}

export function todayIso(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

export function formatDate(value: string | Date, locale = 'bg-BG'): string {
  const date = typeof value === 'string' ? new Date(value) : value;
  return new Intl.DateTimeFormat(locale, { dateStyle: 'long' }).format(date);
}
